/**
 * Search engine client — Prefixbox decides *which* products, Shopify decides what they *cost*.
 *
 * Phase 1 of the Prefixbox Search Migration. Nothing renders here: this file is the data layer
 * that `theme.js` (dropdown) and the search section (results grid) both call, so the two can
 * never disagree again — which was the original bug.
 *
 * Why money is not read from the search index
 * -------------------------------------------
 * The index stores a single base price. This store runs 46 markets with separate Shopify Markets
 * price lists, not conversions: one product is 199.50 USD / 151.00 GBP / 172.95 EUR / ¥32,500.
 * Prefixbox's multi-market `storeId` parameter is currently inert (see docs), so prices are
 * resolved against Shopify instead.
 *
 * Resolving them per search would cost a second round trip — measured at 557ms median against
 * today's 325ms, because the Storefront API's TTFB is ~300ms. Instead we exploit a property of
 * this catalogue: all 839 products sit on **67 distinct base prices**, and base → market price is
 * a pure function (verified across all 67 tiers, 166 products, in DE and GB). So one lookup table
 * per market answers every price, and steady-state search costs exactly one request.
 *
 * The table is built from products we have already seen — every Prefixbox document carries both
 * `priceValue` (base) and `identifier` (the Shopify product id), so each result set is its own
 * sample. Unknown tiers are hydrated in one batched call and cached.
 */

const SearchEngine = (() => {
  const config = (window.theme && window.theme.search) || {};
  const SEARCH_API = 'https://api.prefixbox.com/search/results/';
  const AUTOCOMPLETE_API = 'https://api.prefixbox.com/autocomplete/suggestions';

  // Verified working tokenless from the shop's own domain — no access token in the theme.
  //
  // `storefrontOrigin` is blank in production, where same-origin is correct. It exists for local
  // previews: `theme dev` serves the test store, whose catalogue does not contain the product ids
  // the production search index returns, so prices would silently fail to resolve. Pointing it at
  // the live domain prices local results against the real catalogue. Both APIs send
  // `access-control-allow-origin: *`, so the browser allows it from 127.0.0.1.
  const STOREFRONT_API = `${config.storefrontOrigin || ''}/api/${
    config.storefrontApiVersion || '2026-07'
  }/graphql.json`;

  const PRICE_CACHE_KEY = 'everwow:price-tiers';
  const PRICE_CACHE_TTL = 24 * 60 * 60 * 1000;

  // Prefixbox returns its own placeholder rather than omitting the field.
  const PLACEHOLDER_IMAGE = 'no-image.png';

  // A broad term used only to enumerate price tiers when warming — it needs to match widely, not
  // precisely. Tiers it misses are hydrated on demand, so a narrower match costs one extra call
  // rather than a wrong price.
  const WARM_QUERY = 'gift';

  /**
   * Currencies whose symbol collides with another active Everwow market's, so the ISO code has to
   * be appended. Mirrors the list in snippets/price.liquid — keep the two in step.
   */
  const AMBIGUOUS_CURRENCIES = [
    'USD',
    'CAD',
    'AUD',
    'NZD',
    'SGD',
    'HKD',
    'TWD',
    'MXN',
    'CLP',
    'COP',
    'JMD',
    'SEK',
    'NOK',
    'DKK',
    'JPY',
    'CNY',
  ];

  const priceTiers = new Map();
  const queryCache = new Map();
  const inFlight = new Map();

  // Largest total any query has returned. Used to recognise a query that has matched the whole
  // index — `ball` returns all 839 with a resultFound status. Self-calibrating on purpose: the
  // Liquid-supplied `catalogueSize` counts the *storefront's* products, which is the wrong number
  // whenever the index and the storefront differ (local previews against the production index).
  let observedMax = 0;
  let tierCacheLoaded = false;
  let warming = null;

  const isConfigured = () => Boolean(config.apiKey && config.searchTracker);

  const marketKey = () =>
    `${window.theme?.countryCode || 'XX'}:${
      window.theme?.currencyCode || 'XXX'
    }`;

  /* ------------------------------------------------------------------ money */

  const groupAmount = (value, precision, thousands, decimal) => {
    const fixed = Math.abs(value).toFixed(precision);
    const [whole, fraction] = fixed.split('.');
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    const sign = value < 0 ? '-' : '';

    return fraction
      ? `${sign}${grouped}${decimal}${fraction}`
      : `${sign}${grouped}`;
  };

  /**
   * Formats an amount exactly as `{% render 'price' %}` does, so client-rendered cards never
   * disagree with the rest of the storefront. `window.theme.moneyFormat` already follows the
   * active presentment currency under Shopify Markets, so the market is baked in by Liquid.
   *
   * @param {number|string} amount - Amount in the presentment currency, not minor units.
   * @returns {string}
   */
  const formatMoney = (amount) => {
    const value = typeof amount === 'number' ? amount : parseFloat(amount);
    if (!isFinite(value)) return '';

    const theme = window.theme || {};
    const appendCode =
      AMBIGUOUS_CURRENCIES.includes(theme.currencyCode) &&
      !(theme.currencyCode === 'USD' && theme.countryCode === 'US');

    const format =
      (appendCode ? theme.moneyFormatWithCurrency : theme.moneyFormat) ||
      theme.moneyFormat ||
      '${{amount}}';

    return format.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, placeholder) => {
      switch (placeholder) {
        case 'amount':
          return groupAmount(value, 2, ',', '.');
        case 'amount_no_decimals':
          return groupAmount(value, 0, ',', '.');
        case 'amount_with_comma_separator':
          return groupAmount(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return groupAmount(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator':
          return groupAmount(value, 2, "'", '.');
        case 'amount_with_space_separator':
          return groupAmount(value, 2, ' ', ',');
        case 'amount_no_decimals_with_space_separator':
          return groupAmount(value, 0, ' ', ',');
        default:
          return match;
      }
    });
  };

  /* ------------------------------------------------------------ price tiers */

  const loadTierCache = () => {
    if (tierCacheLoaded) return;
    tierCacheLoaded = true;

    try {
      const raw = localStorage.getItem(PRICE_CACHE_KEY);
      if (!raw) return;

      const cached = JSON.parse(raw);
      if (cached.market !== marketKey()) return;
      if (Date.now() - cached.savedAt > PRICE_CACHE_TTL) return;

      Object.entries(cached.tiers).forEach(([base, tier]) => {
        priceTiers.set(Number(base), tier);
      });
    } catch (error) {
      // A corrupt or unavailable store is not worth failing a search over.
    }
  };

  const saveTierCache = () => {
    try {
      localStorage.setItem(
        PRICE_CACHE_KEY,
        JSON.stringify({
          market: marketKey(),
          savedAt: Date.now(),
          tiers: Object.fromEntries(priceTiers),
        }),
      );
    } catch (error) {
      // Private mode, quota — the map still works for this page view.
    }
  };

  /**
   * Fetches market-correct prices for the given Shopify product ids in one tokenless call.
   *
   * @param {string[]} ids - Numeric Shopify product ids.
   * @returns {Promise<Map<string, object>>} keyed by numeric id
   */
  const hydrate = async (ids) => {
    const unique = [...new Set(ids)].filter(Boolean);
    if (!unique.length) return new Map();

    const country = window.theme?.countryCode || 'US';
    const query = `query($ids: [ID!]!) @inContext(country: ${country}) {
      nodes(ids: $ids) {
        ... on Product {
          id
          title
          handle
          featuredImage { url(transform: { maxWidth: 400 }) altText }
          priceRange { minVariantPrice { amount } }
          compareAtPriceRange { minVariantPrice { amount } }
        }
      }
    }`;

    const response = await fetch(STOREFRONT_API, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        query,
        variables: { ids: unique.map((id) => `gid://shopify/Product/${id}`) },
      }),
    });

    if (!response.ok) throw new Error(`Storefront API ${response.status}`);

    const payload = await response.json();
    const nodes = payload?.data?.nodes || [];
    const products = new Map();

    nodes.forEach((node) => {
      if (!node?.id) return;
      products.set(node.id.split('/').pop(), node);
    });

    return products;
  };

  /**
   * Ensures every base price in `documents` has a market price in the lookup table, hydrating the
   * ones it hasn't seen. Callers may render before awaiting this — prices fill in after.
   *
   * @param {object[]} documents - Normalised documents from `search`.
   */
  const resolvePrices = async (documents) => {
    loadTierCache();

    const missing = new Map();
    documents.forEach((doc) => {
      if (doc.basePrice === null || priceTiers.has(doc.basePrice)) return;
      if (!missing.has(doc.basePrice)) missing.set(doc.basePrice, doc.id);
    });

    if (!missing.size) return;

    const products = await hydrate([...missing.values()]);

    missing.forEach((id, base) => {
      const node = products.get(String(id));
      if (!node) return;

      const price = parseFloat(node.priceRange?.minVariantPrice?.amount);
      const compareAt = parseFloat(
        node.compareAtPriceRange?.minVariantPrice?.amount,
      );

      priceTiers.set(base, {
        price: isFinite(price) ? price : null,
        compareAt: isFinite(compareAt) && compareAt > price ? compareAt : null,
      });
    });

    saveTierCache();
  };

  /**
   * The market price for a base price, or null while the tier is still unknown.
   *
   * @param {number} basePrice - `priceValue` from a Prefixbox document.
   * @returns {{price: string, compareAt: string|null, savePercent: number}|null}
   */
  const priceFor = (basePrice) => {
    loadTierCache();

    const tier = priceTiers.get(basePrice);
    if (!tier || tier.price === null) return null;

    // Rounded down, as on the product page. The 1e-9 keeps an exact 20% from flooring to 19 when
    // the prices carry cents.
    const savePercent = tier.compareAt
      ? Math.floor(
          ((tier.compareAt - tier.price) * 100) / tier.compareAt + 1e-9,
        )
      : 0;

    return {
      price: formatMoney(tier.price),
      compareAt: tier.compareAt ? formatMoney(tier.compareAt) : null,
      savePercent,
    };
  };

  /**
   * Every known tier as `[base, market]` amounts, for a caller that has to reason about prices
   * rather than print one: the directory restates its price bands in the visitor's currency.
   *
   * @returns {Array<[number, number]>}
   */
  const marketPrices = () => {
    loadTierCache();

    return [...priceTiers]
      .filter(([, tier]) => tier && tier.price !== null)
      .map(([base, tier]) => [base, tier.price]);
  };

  /**
   * The "From" qualifier for a document's price, or '' when the price needs no qualifier.
   *
   * Client twin of `snippets/product-price.liquid`, and the same rule the PDP's variant picker
   * uses: a product with more than one variant whose `ew_pdp.product_layer` is not HERO has
   * variants that are a real choice, so its indexed price is the cheapest rather than *the*
   * price. The three have to stay in step.
   *
   * A blank layer only earns the label on the gift card. Prefixbox omits a field it has no value
   * for, so an unindexed `product_layer` and a genuinely empty one look identical from here —
   * and reading "absent" as "not HERO" would put "From" on every multi-variant card in the
   * catalogue the moment the index drifted. The gift card is the one product whose layer is
   * legitimately empty on the store, and `productType` names it without the metafield.
   *
   * @param {object} doc - A normalised document from `search`.
   * @returns {string} The label, or '' for no label.
   */
  const priceFromLabel = (doc) => {
    if (!doc || doc.variantCount <= 1) return '';

    const layer = doc.productLayer.toUpperCase();
    const qualifies = layer ? layer !== 'HERO' : doc.type === 'gift-card';

    return qualifies ? window.theme?.priceFromLabel || 'From' : '';
  };

  /**
   * Warms the lookup table in the background — call it when the search field takes focus, so the
   * table is populated before the shopper finishes typing.
   *
   * Discovery walks the price-sorted index from both ends, which reaches every tier that matters
   * without pulling the whole catalogue.
   */
  const warm = () => {
    if (warming) return warming;
    loadTierCache();
    if (priceTiers.size) return Promise.resolve();
    if (!isConfigured()) return Promise.resolve();

    warming = (async () => {
      try {
        // Walking price-ascending pages covers every tier, where sampling only the two ends
        // leaves the middle of the catalogue unpriced. Four pages of 250 is the whole catalogue
        // today; the cap keeps this bounded if it grows.
        const pages = await Promise.all(
          [1, 2, 3, 4].map((page) =>
            search(WARM_QUERY, {
              page,
              pageSize: 250,
              sort: 'price asc',
            }).catch(() => ({ documents: [] })),
          ),
        );

        const seen = new Map();
        pages.forEach(({ documents }) =>
          documents.forEach((doc) => {
            if (doc.basePrice !== null && !seen.has(doc.basePrice)) {
              seen.set(doc.basePrice, doc);
            }
          }),
        );

        await resolvePrices([...seen.values()]);
      } catch (error) {
        // Warming is an optimisation; a failure just means the first search hydrates instead.
      } finally {
        warming = null;
      }
    })();

    return warming;
  };

  /* ----------------------------------------------------------------- search */

  const normalise = (entry) => {
    const doc = entry.document || entry;
    const image = doc.imageUrl || '';
    const secondImage = doc.secondaryImageUrl || '';
    const basePrice =
      typeof doc.priceValue === 'number'
        ? doc.priceValue
        : parseFloat(doc.priceValue);

    return {
      id: String(doc.identifier || ''),
      // `ew_pdp.clean_display_name`, the short name every card shows; only the product page
      // prints the full title. Prefixbox serves it once the metafield is added to the index;
      // until then, and for a product with no value, the title.
      title: doc.custom_clean_display_name || doc.displayText || '',
      url: doc.url || '',
      image: image.includes(PLACEHOLDER_IMAGE) ? '' : image,
      secondImage: secondImage.includes(PLACEHOLDER_IMAGE) ? '' : secondImage,
      basePrice: isFinite(basePrice) ? basePrice : null,
      rating: doc.custom_reviews_rating ?? null,
      // Appears once `ew_pdp.review_count` has been indexed; null until that sync lands, and the
      // card drops the "(N reviews)" line rather than showing a zero.
      reviewCount: doc.custom_review_count ?? null,
      // The card subline ("Private reef snorkeling, guided marine spotting + more"). Liquid reads
      // this from `product.metafields.collection.product_card_included_experiences`; Prefixbox
      // exposes the same metafield as a `custom_`-prefixed index field, which is the only way the
      // JS-rendered cards can see it — the Storefront API will not serve metafields to a tokenless
      // request. Empty string, not null, so the renderer's truthiness check is the whole story.
      includedExperiences: doc.custom_product_card_included_experiences || '',
      // The card's three points, `collection.product_card_benefit_list`, as written: the Liquid
      // card swaps the live experience count into the first, which matched on every gift when
      // the field was indexed (2026-10-17). An array; a document indexed before then has none.
      benefits: (Array.isArray(doc.custom_product_card_benefit_list)
        ? doc.custom_product_card_benefit_list
        : []
      ).filter((text) => typeof text === 'string' && text.trim()),
      // Shopify's `product.type`, which the index carries verbatim.
      type: doc.productType || '',
      // `ew_pdp.product_layer`, namespace dropped by Prefixbox as every indexed metafield is.
      // Read by `priceFromLabel`; '' when the product has no value for it.
      productLayer: doc.custom_product_layer || '',
      // `skus` is the only per-variant array Prefixbox returns, and its length tracks the variant
      // count even here, where every entry is null. It is how `priceFromLabel` gets at the
      // `variants.size > 1` half of the rule without a Storefront round trip.
      variantCount: (doc.skus || []).length,
      available: doc.availability === 'in stock',
    };
  };

  /**
   * Runs a search against Prefixbox.
   *
   * @param {string} query
   * @param {object} [options]
   * @param {number} [options.page] - 1-based.
   * @param {number} [options.pageSize] - Prefixbox caps this at 250.
   * @param {string} [options.sort] - `<field> asc|desc`. A bare field name is silently ignored by
   *   the API and falls back to relevance, so always pass a direction.
   * @param {string} [options.filter] - Facet expression, passed through untouched.
   * @returns {Promise<{documents: object[], total: number, empty: boolean, sprawl: boolean, corrected: string|null}>}
   */
  const search = async (query, options = {}) => {
    if (!isConfigured()) {
      throw new Error('SearchEngine: missing Prefixbox configuration');
    }

    const page = options.page || 1;
    const pageSize = options.pageSize || 10;
    const sort = options.sort || '';
    const filter = options.filter || '';
    const key = `${query}|${page}|${pageSize}|${sort}|${filter}`;

    if (queryCache.has(key)) return queryCache.get(key);
    if (inFlight.has(key)) return inFlight.get(key);

    const url =
      `${SEARCH_API}?btr=${encodeURIComponent(config.searchTracker)}` +
      `&filter=${encodeURIComponent(filter)}` +
      `&orderby=${encodeURIComponent(sort)}` +
      `&top=${pageSize}&page=${page}` +
      `&useMerchandising=true&pattern=${encodeURIComponent(query)}`;

    const request = (async () => {
      const response = await fetch(url, {
        headers: { 'Ocp-Apim-Subscription-Key': config.apiKey },
      });

      if (!response.ok) throw new Error(`Prefixbox ${response.status}`);

      const payload = await response.json();

      // A misconfigured tracker answers 200 with this status rather than an HTTP error, and an
      // empty `documents` array alongside it. Left unchecked it renders as a confident "0
      // results"; thrown, it falls back to Shopify's own search like any other outage.
      if (payload.resultStatus === 'errorOccured') {
        throw new Error('Prefixbox returned errorOccured');
      }

      const documents = (payload.documents || []).map(normalise);

      // `resultNotFound` is the only reliable empty signal — an empty `documents` array on a
      // later page is not the same thing as no results.
      const empty = payload.resultStatus === 'resultNotFound';
      const total = empty ? 0 : payload.resultCount || 0;
      // Measured *before* this query is folded in. Including its own total first made `indexSize`
      // at least `total` on every call, so `total >= indexSize` collapsed to "biggest query so
      // far" — and on a store where `catalogueSize` resolves to 0 (a theme preview, a sales
      // channel the products are not published to) that made the very first query look like
      // sprawl, permanently hiding the result count.
      const indexSize = Math.max(observedMax, config.catalogueSize || 0);
      observedMax = Math.max(observedMax, total);

      const result = {
        documents,
        total,
        empty,
        // Prefixbox corrects typos silently — `helicoptor` returns helicopter products, but the
        // response carries no corrected term (`searchParameters.query` just echoes the input).
        // So "Showing results for …" cannot be built from a search response; the autocomplete
        // endpoint is the only place a corrected spelling surfaces. Kept for the day they add it.
        corrected: payload.correctedQuery || null,
        facets: payload.filters || [],
        // Some queries match the entire index with a `resultFound` status — `ball` returns all
        // 839. Flagged rather than hidden, so callers can refuse to print the number.
        sprawl: indexSize > 0 && total >= indexSize,
      };

      queryCache.set(key, result);
      inFlight.delete(key);

      return result;
    })();

    inFlight.set(key, request);
    request.catch(() => inFlight.delete(key));

    return request;
  };

  /**
   * Keyword suggestions for the dropdown's Suggestions row.
   *
   * @param {string} query
   * @returns {Promise<{text: string, url: string}[]>}
   */
  const suggest = async (query) => {
    if (!config.apiKey || !config.autocompleteTracker) return [];

    const url =
      `${AUTOCOMPLETE_API}?btr=${encodeURIComponent(
        config.autocompleteTracker,
      )}` + `&pattern=${encodeURIComponent(query)}&highlight=false`;

    try {
      const response = await fetch(url, {
        headers: { 'Ocp-Apim-Subscription-Key': config.apiKey },
      });

      if (!response.ok) return [];

      const payload = await response.json();

      return (payload.suggestions || [])
        .filter((item) => item.type === 'keyword')
        .map((item) => ({ text: item.text, url: item.url }));
    } catch (error) {
      return [];
    }
  };

  return {
    search,
    suggest,
    hydrate,
    resolvePrices,
    priceFor,
    marketPrices,
    priceFromLabel,
    formatMoney,
    warm,
    isConfigured,
  };
})();

window.SearchEngine = SearchEngine;
